import { ButtonColor } from "@/common/enum/button-color";
import { DiscountDto } from "@/types/dto/discount-dto";
import { UserDto } from "@/types/dto/user-dto";
import { Button, Modal, TextInput } from "flowbite-react";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { userService } from "../../services/user-service";
import { CustomHelperText } from "../CustomHelperText";
import { CustomDatepicker } from "../datepicker/CustomDatepicker";
import { TextInputLabel } from "../label/TextInputLabel";
import { H4 } from "../typography/H4";

interface Props {
  user: UserDto;
  checkedItemIds: number[];
  searchItemName: string;
  getPage: (name: string) => void;
  closeModal: () => void;
  setDiscounts: React.Dispatch<React.SetStateAction<DiscountDto[]>>;
  resetCheckedItems: () => void;
}

export const PartnerIncentivesModal: React.FC<Props> = ({
  user,
  checkedItemIds,
  searchItemName,
  getPage,
  closeModal,
  setDiscounts,
  resetCheckedItems,
}) => {
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [rate, setRate] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isNoPeriod, setIsNoPeriod] = useState(false);

  const isSubmitDisabled = isNoPeriod ? !rate : !(startDate && endDate && rate);

  const handleNoPeriodToggle = () => {
    setIsNoPeriod((prev) => !prev);
    if (!isNoPeriod) {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (/^\d*$/.test(value)) {
      setRate(value);
    }
  };

  const handleSubmit = async () => {
    let fromDate, toDate;
    if (isNoPeriod) {
      fromDate = moment
        .tz(startDate, "Asia/Seoul")
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      toDate = undefined;
    } else {
      if (!startDate || !endDate) {
        return;
      }

      fromDate = moment
        .tz(startDate, "Asia/Seoul")
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]");
      toDate = moment
        .tz(endDate, "Asia/Seoul")
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]");
    }
    try {
      const response = await userService.incentiveDiscounts(user.id, {
        fromDate,
        toDate,
        discountRate: Number(rate),
        itemIds: checkedItemIds,
        delete: false,
      });
      if (!response.ok) {
        alert("입력값을 다시 확인해주세요.");
        return;
      }
      setDiscounts((prev) => {
        const newDiscounts = [...prev];
        response.data.forEach((newDiscount: DiscountDto) => {
          const existingIndex = newDiscounts.findIndex(
            (discount) => discount.item?.id === newDiscount.item?.id
          );
          if (existingIndex !== -1) {
            newDiscounts[existingIndex] = newDiscount;
          } else {
            newDiscounts.push(newDiscount);
          }
        });
        return newDiscounts;
      });

      resetCheckedItems();
      closeModal();
    } catch (error) {
      console.error(error);
    }
    // setModal({
    //   open: false,
    //   startDate,
    //   endDate,
    // });
  };

  const handleStartDate = (date: Date) => {
    const startDate = moment(date).startOf("day").toDate();
    setStartDate(startDate);
  };

  const handleEndDate = (date: Date) => {
    const endDate = moment(date).endOf("day").toDate();
    setEndDate(endDate);
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  useEffect(() => {
    setIsExpanded(false);
  }, [startDate, endDate]);

  return (
    <Modal size="2xl" show={true} onClose={closeModal}>
      <Modal.Header className="p-[24px] h-[76px]">
        <H4>인센티브 할인 일괄 적용</H4>
      </Modal.Header>
      <Modal.Body className="overflow-visible">
        <div className="flex justify-center">
          <div className="flex flex-col">
            <div className="flex flex-col gap-[20px]">
              <div className="flex items-center gap-[8px]">
                <div className="flex flex-col w-full" onFocus={handleFocus}>
                  <TextInputLabel
                    required
                    className="mb-[4px] text-semantic-text-heading"
                  >
                    할인 시작일
                  </TextInputLabel>
                  <CustomDatepicker
                    selectedDate={startDate}
                    maxDate={endDate}
                    onDateChange={handleStartDate}
                    disabled={isNoPeriod}
                  />
                </div>
                <div className="flex flex-col w-full" onFocus={handleFocus}>
                  <TextInputLabel
                    required
                    className="mb-[4px] text-semantic-text-heading"
                  >
                    할인 종료일
                  </TextInputLabel>
                  <CustomDatepicker
                    selectedDate={endDate}
                    minDate={startDate}
                    onDateChange={handleEndDate}
                    placeholder="종료일 선택"
                    disabled={isNoPeriod}
                  />
                </div>
              </div>
              <div className="flex items-center gap-[2px]">
                <input
                  type="checkbox"
                  id="noPeriod"
                  name="periodOption"
                  checked={isNoPeriod}
                  onChange={handleNoPeriodToggle}
                  className="hidden" // 기본 체크박스 숨기기
                />
                <label
                  htmlFor="noPeriod"
                  className={`inline-block w-4 h-4 rounded-full border-2 border-gray-400 cursor-pointer ${
                    isNoPeriod ? "bg-black" : "bg-white"
                  }`}
                ></label>
                <TextInputLabel
                  htmlFor="noPeriod"
                  className="ml-2 cursor-pointer"
                  style={{ marginBottom: "2px", pointerEvents: "none" }}
                >
                  기간없음
                </TextInputLabel>
              </div>
              <div>
                <TextInputLabel required>적용할 할인율</TextInputLabel>
                <TextInput
                  className="w-[572px]"
                  placeholder="0%"
                  name="road"
                  value={rate}
                  onChange={handleTextInputChange}
                />
              </div>
              <CustomHelperText
                isError={false}
                text={"기간이 지날 경우 할인율이 0%로 초기화됩니다."}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={isSubmitDisabled}
          color={isSubmitDisabled ? ButtonColor.DISABLED : ButtonColor.PRIMARY}
          onClick={handleSubmit}
          // size={"xl"}
        >
          적용하기
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
